import { Spinner } from '@creditornot/cb-progress';
import { SelectAsync } from '@creditornot/cb-select';
import { useQuery } from '@tanstack/react-query';
import React, { useCallback } from 'react';
import { useField, useFormState } from 'react-final-form';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import { getBrandById, searchActiveBrands } from 'apis/product-information-catalog/api';
import { localize } from 'common/utils';
import { useOfferingMenuPermissionsContext } from 'modules/shared-menu/contexts/OfferingMenuPermissionsContext';

import { BaseField } from './types';
import { FormRowRenderer } from '../FormRowRenderer';
import { DisplayText, StyledInputContainer } from '../OfferDetailsFormFields.styles';
import { FormValues } from '../types';

const SpinnerContainer = styled.div`
  height: 36px;
  display: flex;
  align-items: center;
`;

export const Brand = ({ sidePanelMode, offer }: BaseField) => {
  const menuPermissions = useOfferingMenuPermissionsContext();
  const { values } = useFormState<FormValues>();
  const brandId = offer?.brand_id || values.brandId;
  const intl = useIntl();
  const brandField = useField<FormValues['brandId']>('brandId');

  const { data: brand, isInitialLoading: loadingBrand } = useQuery(
    ['product-brand', brandId],
    () => (!brandId ? Promise.resolve(null) : getBrandById(brandId)),
    { enabled: !!brandId },
  );

  const loadBrandOptions = useCallback(
    (search: string) =>
      searchActiveBrands(search).then(brands =>
        brands.map(brand => ({
          label: localize(brand.name.translations, brand.name.primaryLanguage),
          value: brand.id,
        })),
      ),
    [],
  );

  const previewComponent = (
    <>
      {loadingBrand ? (
        <SpinnerContainer>
          <Spinner size="small" />
        </SpinnerContainer>
      ) : (
        <DisplayText>{brand ? localize(brand.name.translations, brand.name.primaryLanguage) : '-'}</DisplayText>
      )}
    </>
  );

  const editComponent = (
    <StyledInputContainer>
      {loadingBrand ? (
        <SpinnerContainer>
          <Spinner size="small" />
        </SpinnerContainer>
      ) : (
        <SelectAsync
          {...brandField.input}
          clearable
          initialOption={
            brand
              ? { label: localize(brand.name.translations, brand.name.primaryLanguage), value: brand.id }
              : undefined
          }
          loadOptions={loadBrandOptions}
        />
      )}
    </StyledInputContainer>
  );

  return (
    <FormRowRenderer
      hasContentEditPermission={menuPermissions.contentEdit}
      label={intl.formatMessage({ id: 'menu-item.brand' })}
      previewComponent={previewComponent}
      editComponent={editComponent}
      shouldDisplayCreateTag={!values.brandId}
      sidePanelMode={sidePanelMode}
      offer={offer}
    />
  );
};
